import type { Product } from '../core/types/catalog'
import type { CartLine } from '../core/types/cart'
import type { OrderLineVia } from '../core/types/requests'
import { readLocalJson, writeLocalJson } from '../core/storage'
import { clampQty, isSoldOut, maxQty } from '../core/rules/stock'
import { canSubscribe } from '../core/rules/features'
import { currentFeatures } from '../data/features'

/**
 * De winkelmand in de browser. Bewaard als ids + aantallen, nooit als productkopie: bij
 * terugkomst komen prijs en naam altijd uit de actuele catalogus. Valt er iets uit of gaat een
 * aantal omlaag, dan onthouden we dat als `adjustments`: de mand en het afrekenen tonen er één
 * melding over. Een oudere mand zonder `bundle` of `intervalWeeks` laadt gewoon (losse regels,
 * standaardritme).
 */
export const CART_KEY = 'bb-cart-v1'

export type StoredCart = {
  lines: { productId: string; qty: number; subscribe: boolean; routineId?: string; bundle?: boolean; via?: OrderLineVia }[]
  code: string | null
  giftChoices?: Record<string, string>
  /** Eén ritme voor alles wat bijgevuld wordt (weken). Leeg = de standaard uit de instellingen. */
  intervalWeeks?: number | null
}

/**
 * Wat er bij het bijwerken van de mand veranderde. `soldOut` = tijdelijk uitverkocht (kan terugkomen);
 * `removed` = het product staat niet meer in het assortiment (geen naam meer bekend).
 */
export type CartAdjustment =
  | { kind: 'soldOut'; name: string }
  | { kind: 'removed'; name: string | null }
  | { kind: 'reduced'; name: string; qty: number }

export type Hydrated = { lines: CartLine[]; adjustments: CartAdjustment[] }

/** Alleen een bekende herkomst overnemen uit de opgeslagen mand. */
export const viaOf = (v: unknown): { via?: OrderLineVia } => (v === 'huidadvies' ? { via: v } : {})

/** Alleen een bundel als er ook een protocol bij staat. */
const bundleOf = (l: { bundle?: unknown; routineId?: unknown }): { bundle?: true } =>
  l.bundle === true && typeof l.routineId === 'string' && l.routineId ? { bundle: true } : {}

export const readStoredCart = () => readLocalJson<StoredCart | null>(CART_KEY, null)

export function writeStoredCart(lines: CartLine[], code: string | null, giftChoices: Record<string, string>, intervalWeeks: number | null) {
  const data: StoredCart = {
    lines: lines.map((l) => ({ productId: l.product.id, qty: l.qty, subscribe: l.subscribe, routineId: l.routineId, ...bundleOf(l), ...viaOf(l.via) })),
    code,
    giftChoices,
    intervalWeeks,
  }
  // Privémodus of volle opslag: de mand werkt gewoon, alleen zonder bewaren.
  writeLocalJson(CART_KEY, data)
}

/**
 * De bewaarde mand (gedeeld tussen tabbladen), los van de catalogus: voor de terugkeerpagina, die
 * moet weten of de mand nog bij de betaalde bestelling hoort vóórdat de catalogus geladen is.
 */
export function storedCartLines(): { productId: string; qty: number; subscribe: boolean }[] {
  const lines = readStoredCart()?.lines
  return Array.isArray(lines) ? lines.filter((l) => l && typeof l.productId === 'string').map((l) => ({ productId: l.productId, qty: Number(l.qty) || 0, subscribe: Boolean(l.subscribe) })) : []
}

export const parseStoredCart = (raw: string | null): StoredCart | null => {
  if (!raw) return null
  try {
    return JSON.parse(raw) as StoredCart
  } catch {
    return null
  }
}

/**
 * Opgeslagen mand → regels, met een geldig aantal (ook als localStorage beschadigd of aangepast is).
 * Een product dat niet meer bestaat gaat eruit. Een uitverkocht product blijft staan, net als
 * wanneer het tijdens het winkelen uitverkoopt (`clampToStock`): het kan zo weer leverbaar zijn,
 * en de mand en het afrekenen vragen het dan eerst weg te halen. Eén plek, gebruikt bij het
 * opstarten én bij een wijziging vanuit een ander tabblad.
 */
export const hydrateLines = (stored: StoredCart | null, productById: (id: string) => Product | undefined): Hydrated => {
  const adjustments: CartAdjustment[] = []
  const lines = (stored && Array.isArray(stored.lines) ? stored.lines : []).flatMap((l): CartLine[] => {
    const product = productById(l.productId)
    if (!product) {
      // Een onleesbare regel (geen id) is geen product dat de klant koos: niets over melden.
      if (typeof l.productId === 'string' && l.productId) adjustments.push({ kind: 'removed', name: null })
      return []
    }
    if (isSoldOut(product)) {
      adjustments.push({ kind: 'soldOut', name: product.name })
      const qty = typeof l.qty === 'number' && Number.isFinite(l.qty) ? Math.max(1, Math.floor(l.qty)) : 1
      return [{ product, qty, subscribe: false, routineId: l.routineId, ...bundleOf(l), ...viaOf(l.via) }]
    }
    const qty = clampQty(product, l.qty)
    if (qty > 0 && typeof l.qty === 'number' && l.qty > qty) adjustments.push({ kind: 'reduced', name: product.name, qty })
    return qty > 0
      ? [{ product, qty, subscribe: Boolean(l.subscribe) && canSubscribe(currentFeatures(), product), routineId: l.routineId, ...bundleOf(l), ...viaOf(l.via) }]
      : []
  })
  return { lines, adjustments }
}

/**
 * Komt de catalogus opnieuw binnen met minder voorraad, dan gaat een te hoog aantal omlaag.
 * Uitverkocht blijft wel in de mand staan (met de bestaande "haal eerst weg"-melding),
 * want dat lost de klant zelf op en het kan zo weer leverbaar zijn.
 */
export const clampToStock = (lines: CartLine[], productById: (id: string) => Product | undefined): Hydrated => {
  const adjustments: CartAdjustment[] = []
  const next = lines.map((l) => {
    const product = productById(l.product.id)
    if (!product || isSoldOut(product)) return l
    const qty = Math.min(l.qty, maxQty(product))
    if (qty === l.qty) return l
    adjustments.push({ kind: 'reduced', name: product.name, qty })
    return { ...l, qty }
  })
  return { lines: adjustments.length ? next : lines, adjustments }
}
